import { z } from 'zod';
import { RACQUET_SPORT_IDS } from '../constants/racquetSports';

export const CLUB_OFFERINGS = [
  'court_hire',
  'memberships',
  'inventory_hire',
  'academy_court_access',
] as const;
export type ClubOffering = (typeof CLUB_OFFERINGS)[number];

export const CLUB_COURT_COUNT_BANDS = ['1-4', '5-12', '13+'] as const;
export type ClubCourtCountBand = (typeof CLUB_COURT_COUNT_BANDS)[number];

export const CLUB_LOCATION_COUNT_BANDS = ['1', '2-3', '4+'] as const;
export type ClubLocationCountBand = (typeof CLUB_LOCATION_COUNT_BANDS)[number];

const clubSportSelectionSchema = z.object({
  sportId: z.enum(RACQUET_SPORT_IDS),
  customLabel: z.string().max(80).optional().nullable(),
  isPrimary: z.boolean().optional(),
});

export const clubOperatingProfileSchema = z
  .object({
    sports: z.array(clubSportSelectionSchema).min(1).max(6),
    offerings: z.array(z.enum(CLUB_OFFERINGS)).min(1),
    courtCountBand: z.enum(CLUB_COURT_COUNT_BANDS).optional().nullable(),
    locationCountBand: z.enum(CLUB_LOCATION_COUNT_BANDS).optional().nullable(),
    needsOnlinePayments: z.boolean().optional(),
    needsBrandedSubdomain: z.boolean().optional(),
    onboardingProfileCompletedAt: z.string().datetime().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    const primaryCount = data.sports.filter((s) => s.isPrimary).length;
    if (primaryCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exactly one sport must be marked as primary',
        path: ['sports'],
      });
    }
    const other = data.sports.find((s) => s.sportId === 'other');
    if (other && !other.customLabel?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Custom label is required when sport is Other',
        path: ['sports'],
      });
    }
  });

export type ClubOperatingProfileInput = z.infer<typeof clubOperatingProfileSchema>;

export const createClubDetailsSchema = z.object({
  clubName: z.string().min(1).max(255),
  country: z.string().length(2),
  stateOrProvince: z.string().max(10).optional().nullable(),
  timezone: z.string().min(1).max(64),
});

export type CreateClubDetailsInput = z.infer<typeof createClubDetailsSchema>;

export const createOrganizationSchema = z.object({
  operationType: z.enum(['academy', 'club', 'both']),
  academyName: z.string().min(1).max(255).optional(),
  academyCountry: z.string().length(2).optional(),
  academyStateOrProvince: z.string().max(10).optional().nullable(),
  academyTimezone: z.string().min(1).max(64).optional(),
  academyOperatingProfile: z.any().optional().nullable(),
  clubName: z.string().min(1).max(255).optional(),
  clubCountry: z.string().length(2).optional(),
  clubStateOrProvince: z.string().max(10).optional().nullable(),
  clubTimezone: z.string().min(1).max(64).optional(),
  clubOperatingProfile: z.any().optional().nullable(),
});

export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;
